'use client';

import type { ReactNode } from 'react';
import dynamic from 'next/dynamic';
import { ScreenLayoutProvider } from '@/screen';
import { ScrollManagerProvider } from '@/contexts/ScrollManagerContext';
import { TabBarProvider } from '@/contexts/TabBarContext';
import { DesktopContentWidthProvider } from '@/contexts/DesktopContentWidthContext';
import { ServerAuthProvider } from '@/contexts/ServerAuthContext';
import { ViewportProvider, useIsDesktop } from '@/contexts/ViewportContext';
import { DesktopFrame } from '@/components/desktop/DesktopFrame';
import { DesktopHeader } from '@/components/desktop/DesktopHeader';
import { DesktopNav } from '@/components/desktop/DesktopNav';
import { useIsAuthed } from '@/hooks/useIsAuthed';

const SupportWidget = dynamic(() => import('@/components/support/SupportWidget').then((m) => m.SupportWidget), {
  ssr: false,
});

function LightFrame({ children }: { children: ReactNode }) {
  const isDesktop = useIsDesktop();
  const isAuthed = useIsAuthed();

  return (
    <DesktopFrame
      chrome={
        isDesktop && (
          // WHY: до гидратации разметка одна на телефон и десктоп — на узком экране шапку прячет CSS, а не рендер
          <div className="contents max-lg:hidden">
            <DesktopHeader />
            <DesktopNav />
          </div>
        )
      }
    >
      <ScrollManagerProvider>
        <TabBarProvider>
          <ScreenLayoutProvider>
            {children}
            {isAuthed && <SupportWidget />}
          </ScreenLayoutProvider>
        </TabBarProvider>
      </ScrollManagerProvider>
    </DesktopFrame>
  );
}

// WHY: каркас вики и 404 неизвестного адреса — шапка и меню SHOT без ленты дропов, подписок и игровых провайдеров (app). HTML
// общий для всех гостей (кэш CDN), поэтому ни кука входа, ни подсказка о ширине экрана в него не попадают: сервер рисует
// десктоп и гостя, настоящие ширина и вход приходят после гидратации
export function LightShell({ children }: { children: ReactNode }) {
  return (
    <ViewportProvider initial="desktop">
      <ServerAuthProvider hasAuthCookie={null}>
        <DesktopContentWidthProvider>
          <LightFrame>{children}</LightFrame>
        </DesktopContentWidthProvider>
      </ServerAuthProvider>
    </ViewportProvider>
  );
}
